import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useToast } from "../../hooks/use-toast";
import { timestamp2string } from "../../helpers";
import { useQueryStore } from "../../store/queryStore";
import { Card } from "../../components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { Button } from "../../components/ui/button";
import { ChevronDown, ArrowLeft, RefreshCw } from "lucide-react";
import { FluentEmoji } from "@lobehub/fluent-emoji";
import StatsCards from "../../components/StatsCards";
import RecentLogs from "../../components/RecentLogs";
import Papa from "papaparse";

// 动画变体定义
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1], // Apple 风格的贝塞尔曲线
      staggerChildren: 0.15, // 增加间隔时间，让层次感更明显
      delayChildren: 0.1, // 添加子元素延迟
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
    },
  },
};

const navVariants = {
  initial: {
    opacity: 0,
    y: -30, // 增加初始位移
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
};

const cardVariants = {
  initial: {
    opacity: 0,
    y: 40, // 增加初始位移
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
};

// 添加内容容器动画变体
const contentVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.4,
      delay: 0.2,
      staggerChildren: 0.2, // 子元素间隔0.2秒展示
      delayChildren: 0.3, // 子元素延迟0.3秒开始
    },
  },
};

// 添加背景动画变体
const backgroundVariants = {
  initial: {
    opacity: 0,
    scale: 0.8,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 1.2,
      ease: "easeOut",
    },
  },
};

const refreshVariants = {
  initial: { rotate: 0 },
  animate: {
    rotate: 360,
    transition: {
      duration: 1,
      ease: "linear",
      repeat: Infinity,
    },
  },
};

const Search = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Zustand store
  const {
    selectedService,
    queryData,
    isLoading,
    error,
    hasValidKey,
    hasQueryData,
    hasSavedKey,
    performQuery,
    refreshData,
    clearAll,
  } = useQueryStore();

  const [refreshKey, setRefreshKey] = useState(0);
  const [statsData, setStatsData] = useState({
    balance: 0,
    usage: 0,
    logs: [],
    tokenValid: false,
  });
  const [statsCollapsed, setStatsCollapsed] = useState(false);
  const [logsCollapsed, setLogsCollapsed] = useState(false);

  // 添加问候语 useMemo
  const greetingText = useMemo(() => {
    const h = new Date().getHours();
    let greet;
    if (h >= 5 && h < 12) greet = "早上好";
    else if (h >= 12 && h < 14) greet = "中午好";
    else if (h >= 14 && h < 18) greet = "下午好";
    else greet = "晚上好";
    return `欢迎回来，👋${greet}`;
  }, []);

  // 检查是否有有效会话，如果没有就返回首页
  useEffect(() => {
    // 检查是否有当前输入的有效Key或者已保存的有效Key
    if (!hasValidKey() && !hasSavedKey()) {
      navigate("/");
      return;
    }

    // 如果有查询数据，设置到本地状态
    if (hasQueryData()) {
      setStatsData(queryData);
    }
  }, [hasValidKey, hasSavedKey, hasQueryData, queryData, navigate]);

  // 处理刷新数据
  const handleRefresh = async () => {
    try {
      await refreshData();
      setRefreshKey((prevKey) => prevKey + 1);
      toast({
        title: "刷新成功",
        description: "数据已更新",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "刷新失败",
        description: error.message || "刷新过程中发生错误",
        variant: "destructive",
      });
    }
  };

  const exportCSV = () => {
    const csvData = statsData.logs.map((log) => ({
      时间: timestamp2string(log.created_at),
      模型: log.model_name,
      用时: log.use_time,
      输入: log.prompt_tokens,
      输出: log.completion_tokens,
      花费: "$" + (log.quota / 500000).toFixed(5),
    }));

    const csvString = "\ufeff" + Papa.unparse(csvData);
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "smallai_usage_data.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  // 返回首页的处理函数
  const handleGoBack = () => {
    navigate("/");
  };

  // 清除缓存并返回首页
  const handleClearAndGoBack = () => {
    clearAll();
    navigate("/");
  };

  return (
    <motion.div
      className="min-h-screen bg-gray-50 dark:bg-gray-950"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit">
      {/* OpenAI 风格的光晕效果 */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 dark:bg-blue-400/5 rounded-full filter blur-3xl"
          variants={backgroundVariants}
          initial="initial"
          animate="animate"
          style={{
            x: 0,
            y: 0,
            scale: 1,
          }}
          transition={{
            duration: 1.5,
            delay: 0.5,
            ease: "easeOut",
          }}
          onAnimationComplete={() => {
            // 动画完成后开始循环动画
            setTimeout(() => {
              // 这里可以启动循环动画
            }, 500);
          }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/10 dark:bg-purple-400/5 rounded-full filter blur-3xl"
          variants={backgroundVariants}
          initial="initial"
          animate="animate"
          transition={{
            duration: 1.5,
            delay: 0.8,
            ease: "easeOut",
          }}
        />
      </div>

      {/* 顶部导航栏 - Apple 风格的毛玻璃效果 */}
      <motion.div
        className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-2xl border-b border-gray-200/20 dark:border-gray-800/20"
        variants={navVariants}>
        <div className="w-full px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGoBack}
                  className="flex items-center gap-1 sm:gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 px-2 sm:px-3 transition-colors duration-200">
                  <motion.div
                    initial={{ x: 0 }}
                    whileHover={{ x: -3 }}
                    transition={{ type: "spring", stiffness: 300 }}>
                    <ArrowLeft className="h-4 w-4" />
                  </motion.div>
                  <span className="hidden sm:inline">返回</span>
                </Button>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAndGoBack}
                  className="flex items-center gap-1 sm:gap-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-2 sm:px-3 transition-colors duration-200"
                  title="清除缓存记录并返回首页">
                  <span className="text-xs sm:text-sm">🗑️</span>
                  <span className="hidden sm:inline text-xs">清除</span>
                </Button>
              </motion.div>
              <motion.div
                className="hidden sm:block h-6 w-px bg-gray-300 dark:bg-gray-700"
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              />
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}>
                <h1 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate max-w-32 sm:max-w-none">
                  {selectedService.name || "查询结果"}
                </h1>
              </motion.div>
            </div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 transition-all duration-200 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600">
                <motion.div
                  variants={refreshVariants}
                  animate={isLoading ? "animate" : "initial"}>
                  <RefreshCw className="h-4 w-4" />
                </motion.div>
                <span className="hidden sm:inline">刷新</span>
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* 内容区域 - Google Material Design 风格的层次感 */}
      <motion.div
        className="w-full px-4 sm:px-6 py-6 sm:py-8"
        variants={contentVariants}
        initial="initial"
        animate="animate">
        <motion.div
          className="space-y-4 sm:space-y-6"
          variants={contentVariants}>
          {/* 问候语区域 */}
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}>
            <motion.h2
              className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-gray-800 dark:text-gray-200"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              whileHover={{ scale: 1.05 }}>
              {greetingText}
            </motion.h2>
            {/* 如果没有查询数据，显示提示 */}
            {!hasQueryData() && (
              <motion.p
                className="mt-2 text-sm text-gray-500 dark:text-gray-400"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}>
                页面刷新后数据已清空，请点击右上角刷新按钮重新获取数据
              </motion.p>
            )}
          </motion.div>

          {/* 错误提示 */}
          {error && (
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg border border-red-200 dark:border-red-800">
                <span>❌</span>
                <span className="text-sm">{error}</span>
              </div>
            </motion.div>
          )}

          {/* 统计卡片区域 - Spotify 风格的动态卡片 */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -4,
              transition: { duration: 0.2 },
            }}>
            <Collapsible
              open={!statsCollapsed}
              onOpenChange={(open) => setStatsCollapsed(!open)}>
              <Card className="w-full border border-gray-200/50 dark:border-gray-700/50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                <CollapsibleTrigger asChild>
                  <motion.div
                    className="flex items-center justify-between p-4 sm:p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-200 border-b border-gray-200 dark:border-gray-700"
                    whileHover={{ backgroundColor: "rgba(249, 250, 251, 0.8)" }}
                    whileTap={{ scale: 0.995 }}>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        initial={{ rotate: 0, scale: 0.8 }}
                        animate={{ rotate: 0, scale: 1 }}>
                        <FluentEmoji
                          emoji="🪄"
                          type="anim"
                          size={32}
                          cdn="unpkg"
                        />
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}>
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Key 统计概览
                        </h2>
                      </motion.div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <motion.span
                        className="hidden sm:inline text-sm text-gray-500 dark:text-gray-400"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}>
                        {statsCollapsed ? "展开" : "收起"}
                      </motion.span>
                      <motion.div
                        animate={{ rotate: statsCollapsed ? 0 : 180 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        initial={{ scale: 0.8 }}
                        whileHover={{ scale: 1.1 }}>
                        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 dark:text-gray-400" />
                      </motion.div>
                    </div>
                  </motion.div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <motion.div
                    className="p-4 sm:p-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}>
                    <StatsCards
                      key={refreshKey}
                      data={statsData}
                      loading={isLoading}
                    />
                  </motion.div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </motion.div>

          {/* 日志表格区域 - Apple 风格的细节动画 */}
          <motion.div
            variants={cardVariants}
            whileHover={{
              y: -4,
              transition: { duration: 0.2 },
            }}>
            <Collapsible
              open={!logsCollapsed}
              onOpenChange={(open) => setLogsCollapsed(!open)}>
              <Card className="w-full border border-gray-200/50 dark:border-gray-700/50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                <CollapsibleTrigger asChild>
                  <motion.div
                    className="flex items-center justify-between p-4 sm:p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-200 border-b border-gray-200 dark:border-gray-700"
                    whileHover={{ backgroundColor: "rgba(249, 250, 251, 0.8)" }}
                    whileTap={{ scale: 0.995 }}>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: -5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        initial={{ rotate: 0, scale: 0.8 }}
                        animate={{ rotate: 0, scale: 1 }}>
                        <FluentEmoji
                          emoji="♾️"
                          type="anim"
                          size={32}
                          cdn="unpkg"
                        />
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}>
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                          API 调用日志
                        </h2>
                      </motion.div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <motion.span
                        className="hidden sm:inline text-sm text-gray-500 dark:text-gray-400"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8 }}>
                        {logsCollapsed ? "展开" : "收起"}
                      </motion.span>
                      <motion.div
                        animate={{ rotate: logsCollapsed ? 0 : 180 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        initial={{ scale: 0.8 }}
                        whileHover={{ scale: 1.1 }}>
                        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 dark:text-gray-400" />
                      </motion.div>
                    </div>
                  </motion.div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <motion.div
                    className="p-0"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}>
                    <RecentLogs
                      data={statsData}
                      loading={isLoading}
                      onExportCSV={exportCSV}
                    />
                  </motion.div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Search;
